/**
 * `/nfl/teams/[team]` slug: city and mascot, the way a reader writes a team —
 * `dallas-cowboys`, `new-york-giants`, `san-francisco-49ers`.
 *
 * One definition, so both the NFL team directory (`nflTeamDirectoryData`) and
 * `utils/teams`' own href helper spell a slug the same way. Two copies of this
 * rule would mean links that 404.
 *
 * It sits in its own leaf module rather than in `utils/teams`, which is where it
 * used to live, because of what importing `utils/teams` costs. That module reaches
 * `models/market` (and through it `decimal.js`) and `utils/colors` → the 580KB
 * `constants/colorsPalettes` table. `nflTeamDirectoryData` needs nothing from it
 * but this five-line function, and the matchup registry above it is imported by
 * `src/proxy.ts` — so that single import edge dragged ~785KB of source into the
 * *edge middleware* bundle, which every matched request in the app pays to parse.
 * Measured on two production builds of the same tree: the proxy chunk went from
 * 945KB raw / 168KB gzip with the edge to 390KB / 103KB without it. What the
 * matchup registry itself costs there — the JSON, this file and the team table —
 * is ~52KB of source, which is the price the import was supposed to carry.
 *
 * `utils/teams` re-exports it, so every existing import site is unchanged and
 * nothing has to know this file exists. Keep it dependency-free — that is the
 * whole point of it.
 */
export const toNflTeamPageSlug = (team: { market?: string; teamName?: string }): string =>
    `${team.market ?? ''} ${team.teamName ?? ''}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
